import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plane, MessageCircle, Menu, X } from "lucide-react";
import { waLink, DEFAULT_WA_MESSAGE } from "@/config";

const LINKS = [
  { label: "Início", href: "#inicio", id: "inicio" },
  { label: "Serviços", href: "#servicos", id: "servicos" },
  { label: "Loja", href: "#loja", id: "loja" },
  { label: "Cotação", href: "#cotacao", id: "cotacao" },
  { label: "Galeria", href: "#galeria", id: "galeria" },
  { label: "Depoimentos", href: "#depoimentos", id: "depoimentos" },
  { label: "Contato", href: "#contato", id: "contato" },
];

const scrollTo = (e, href, closeMenu) => {
  e.preventDefault();
  if (closeMenu) closeMenu();
  document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? "bg-navy/85 backdrop-blur-xl border-b border-white/10 shadow-2xl py-3"
          : "bg-transparent py-6"
      }`}
    >
      <div className="px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto flex items-center justify-between">
        <a
          href="#inicio"
          data-testid="nav-logo"
          onClick={(e) => scrollTo(e, "#inicio")}
          className="flex items-center gap-3 group"
        >
          <span className="w-10 h-10 rounded-full bg-gold flex items-center justify-center transition-transform duration-500 group-hover:rotate-12">
            <Plane className="w-5 h-5 text-navy" strokeWidth={2.4} />
          </span>
          <span className="leading-none">
            <span className="block font-serif text-2xl font-bold text-ivory tracking-tight">
              A.S.F
            </span>
            <span className="block font-mono text-[10px] uppercase tracking-[0.35em] text-gold">
              Viagens
            </span>
          </span>
        </a>

        <nav className="hidden lg:flex items-center gap-8" data-testid="nav-links">
          {LINKS.map((l) => (
            <a
              key={l.id}
              href={l.href}
              data-testid={`nav-link-${l.id}`}
              onClick={(e) => scrollTo(e, l.href)}
              className="text-sm font-medium text-ivory/70 hover:text-gold transition-colors duration-300 tracking-wide"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={waLink(DEFAULT_WA_MESSAGE)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-cta-button"
            className="hidden sm:inline-flex items-center gap-2 bg-gold text-navy font-semibold text-sm px-5 py-2.5 rounded-full hover:bg-gold-light hover:-translate-y-0.5 transition-all duration-300 shadow-[0_8px_24px_rgba(212,175,55,0.35)]"
          >
            <MessageCircle className="w-4 h-4" />
            Solicitar Cotação
          </a>
          <button
            data-testid="mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-ivory p-2"
            aria-label="Abrir menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-navy/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {LINKS.map((l) => (
                <a
                  key={l.id}
                  href={l.href}
                  data-testid={`mobile-nav-link-${l.id}`}
                  onClick={(e) => scrollTo(e, l.href, () => setOpen(false))}
                  className="text-lg font-serif text-ivory/85 hover:text-gold transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={waLink(DEFAULT_WA_MESSAGE)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="mobile-nav-cta"
                className="mt-2 inline-flex items-center justify-center gap-2 bg-gold text-navy font-semibold px-5 py-3 rounded-full"
              >
                <MessageCircle className="w-4 h-4" />
                Solicitar Cotação
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
